import { useEffect, useMemo, useState } from "react";
import { Alert, AlertDescription } from "../../../app/components/ui/alert";
import { Button } from "../../../app/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../app/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../app/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../app/components/ui/table";
import { TRACKING_EVENTS } from "../../../constants/trackingEvents";
import { api, ApiError } from "../../../lib/api";
import { trackEvent } from "../../../lib/tracking";
import { useAuth } from "../../../providers/AuthProvider";
import type { Product } from "../hooks/useProducts";

type BatchRow = {
  id: string;
  received_at: string | null;
  quantity_remaining: string;
  selling_price: string;
  mrp: string;
  purchase_price: string;
};

const FIFO_VALUE = "__fifo__";

function qtyToNumber(q: string) {
  const n = Number(q);
  return Number.isFinite(n) ? n : 0;
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: Product[];
  onDone: () => Promise<void> | void;
};

export function BulkPosBatchModal({ open, onOpenChange, products, onDone }: Props) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [batchesByProduct, setBatchesByProduct] = useState<Record<string, BatchRow[]>>({});
  const [choice, setChoice] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open || !token || products.length === 0) return;

    setLoading(true);
    setError(null);
    setBatchesByProduct({});
    setChoice({});

    void Promise.all(
      products.map((p) =>
        api.products
          .listStockBatches({ token, productId: p.id })
          .then((res) => ({ id: p.id, rows: res.data })),
      ),
    )
      .then((results) => {
        const map: Record<string, BatchRow[]> = {};
        const nextChoice: Record<string, string> = {};
        for (const r of results) {
          map[r.id] = r.rows;
          const p = products.find((x) => x.id === r.id);
          if (p?.is_override && p.pos_override_batch_id) {
            const still = r.rows.some((b) => b.id === p.pos_override_batch_id);
            nextChoice[r.id] = still ? String(p.pos_override_batch_id) : FIFO_VALUE;
          } else {
            nextChoice[r.id] = FIFO_VALUE;
          }
        }
        setBatchesByProduct(map);
        setChoice(nextChoice);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load batches"))
      .finally(() => setLoading(false));
  }, [open, token, products]);

  const canApply = useMemo(() => {
    return products.length > 0 && Object.keys(choice).length === products.length;
  }, [products.length, choice]);

  const apply = async () => {
    if (!token || !canApply) return;
    setSubmitting(true);
    setError(null);
    try {
      for (const p of products) {
        const c = choice[p.id];
        if (!c) continue;
        if (c === FIFO_VALUE) {
          await api.products.clearPosBatch({ token, productId: p.id });
          trackEvent(TRACKING_EVENTS.FIFO_AUTO_APPLIED);
        } else {
          try {
            await api.products.setPosBatch({ token, productId: p.id, batchId: c });
            trackEvent(TRACKING_EVENTS.PRICING_OVERRIDE_USED);
          } catch (e) {
            if (e instanceof ApiError) {
              const errors = (e.data as { errors?: Record<string, string[]> })?.errors;
              const msg = errors?.batch_id?.[0] ?? e.message;
              throw new Error(`${p.name}: ${msg}`);
            }
            throw e;
          }
        }
      }
      await onDone();
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update POS batches");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Set POS batches</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Choose <span className="font-medium text-foreground">Use FIFO</span> to clear the override (same as the single-product modal), or pick a batch with stock.
          </p>
        </DialogHeader>

        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <div className="border rounded-md overflow-auto min-h-[120px] max-h-[55vh]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>POS batch</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={2} className="text-muted-foreground">
                    Loading batches…
                  </TableCell>
                </TableRow>
              ) : (
                products.map((p) => {
                  const batches = batchesByProduct[p.id] ?? [];
                  const value = choice[p.id] ?? FIFO_VALUE;
                  return (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium align-top">{p.name}</TableCell>
                      <TableCell className="align-top min-w-[220px]">
                        <Select
                          value={value}
                          onValueChange={(v) => setChoice((prev) => ({ ...prev, [p.id]: v }))}
                          disabled={submitting}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose batch" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={FIFO_VALUE}>Use FIFO</SelectItem>
                            {batches.map((b) => {
                              const remaining = qtyToNumber(b.quantity_remaining);
                              const out = remaining <= 0;
                              return (
                                <SelectItem key={b.id} value={b.id} disabled={out}>
                                  Batch #{b.id} · Sell {b.selling_price} · Left {b.quantity_remaining}
                                  {out ? " (out of stock)" : ""}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void apply()} disabled={submitting || loading || !canApply}>
            {submitting ? "Applying…" : "Apply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
